import React, { useState, useEffect, useMemo, Fragment } from 'react';
import styled from 'styled-components';
import {
  has,
  get,
  map,
  size,
  head,
  find,
  keyBy,
  getOr,
  filter,
  omitBy,
  isNull,
  isEmpty
} from 'lodash/fp';
import { Message, Loader, Grid, Row, Col } from 'rsuite';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  getOrderForVisitTrackId,
  getFetchingError as getFetchOrderError,
  getIsFetched as getIsFetchedOrder,
  getIsFetching as getIsFetchingOrder,
  getIsSaving as getIsSavingOrder
} from '../../../redux/POS/selectors/order';
import SearchForm from './SearchForm';
import SearchResults from './SearchResults';
import OrderItems from './OrderItems';
import {
  fetchPosOrderByVisitTrackId,
  createPosOrder,
  addPosOrderItem,
  updatePosOrderItemQuantity,
  searchPosItems,
  fetchPosLocationsForTrackingMethod,
  fetchClient
} from '../../../redux/sagas/routines';

import PosLayout from './Layout';
import { parseInt } from '../../../helpers/utils';
import { COMPLETED } from '../../../constants/pos/OrderStatus';
import {
  setOrderAdjustment,
  addItemAdjustment,
  updateItemAdjustment,
  clearPosSearchItems
} from '../../../redux/POS/actions';
import { removeGuest } from '../../../redux/actions/components/guest-list';
import getLocalOrderAdjustment from '../../../helpers/pos/getLocalOrderAdjustment';
import {
  getIsFetched as getIsFetchedResult,
  getPosSearchItemsList
} from '../../../redux/POS/selectors/searchItems';
import {
  getPosLocationsForTrackingMethod,
  getIsFetched as getIsFetchedCustomLocations
} from '../../../redux/Settings/selectors/pos/trackingMethodLocations';
import { getOrderItemsWithAdjustment } from '../../../redux/POS/selectors/orderAdjustment';
import { getGuestForId } from '../../../redux/selectors/components/guest-list';
import GuestDetails from './GuestDetails';

const toLocationOptions = map(
  ({ inventoryLocation: { id: value, name: label } }) => ({ value, label })
);

const StyledLoader = styled(Loader)`
  z-index: 9;
`;

const StyledCol = styled(Col)`
  @media (min-width: 720px) {
    width: 50%;
  }
`;

const HelpDesiredDetails = styled.div`
  font-size: 14px;
  margin: 3px;
`;

export default function PosOrder() {
  const dispatch = useDispatch();
  const visitTrackId = parseInt(get('visitTrackId', useParams()));

  const [isScanSearch, setIsScanSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState({
    q: null,
    inventoryLocationId: null
  });
  const isOrderFetched = useSelector(getIsFetchedOrder);
  const isFetchingOrder = useSelector(getIsFetchingOrder);
  const fetchOrderError = useSelector(getFetchOrderError);
  const isFetchedResult = useSelector(getIsFetchedResult);
  const isSavingOrder = useSelector(getIsSavingOrder);
  const isFetchedCustomLocations = useSelector(getIsFetchedCustomLocations);
  const order = useSelector(state =>
    getOrderForVisitTrackId(state, visitTrackId)
  );
  const trackingMethodId = get('trackingMethodId', order);
  const clientId = get('clientId', order);
  const customLocations = useSelector(state =>
    getPosLocationsForTrackingMethod(state, trackingMethodId)
  );
  const guest = useSelector(state => getGuestForId(state, clientId));
  const customLocationOptions = useMemo(
    () => (!isEmpty(customLocations) ? toLocationOptions(customLocations) : []),
    [customLocations]
  );
  const startingLocation = useMemo(
    () => {
      if (!isFetchedCustomLocations) return undefined;
      const defaultLocation = find('isDefault', customLocations);
      return getOr(null, 'inventoryLocation.id', defaultLocation);
    },
    [customLocations, isFetchedCustomLocations]
  );

  const orderStatus = parseInt(get('status', order));
  const searchResultItems = useSelector(getPosSearchItemsList);
  const searchResults = useMemo(
    () => {
      if (isEmpty(customLocationOptions || searchResultItems))
        return searchResultItems;

      const customLocationById = keyBy('value', customLocationOptions);
      return filter(
        ({ location }) => has(location.id, customLocationById),
        searchResultItems
      );
    },
    [customLocationOptions, searchResultItems]
  );

  const orderItems = useSelector(state =>
    getOrderItemsWithAdjustment(state, getOr([], 'items', order))
  );

  const getOrderItemByAvailability = itemAvailability => {
    const inventoryItemId = get('item.id', itemAvailability);
    const inventoryLocationId = get('location.id', itemAvailability);
    const inventoryLocationZoneId = get('zone.id', itemAvailability);
    const findQuery = inventoryLocationZoneId
      ? { inventoryItemId, inventoryLocationId, inventoryLocationZoneId }
      : { inventoryItemId, inventoryLocationId };
    return find(findQuery, orderItems);
  };

  const addOrderItem = itemAvailability => {
    const quantity = 1;
    const orderId = order.id;
    if (orderStatus === COMPLETED) {
      dispatch(addItemAdjustment({ orderId, itemAvailability, quantity }));
    } else {
      const itemId = get('item.id', itemAvailability);
      const locationId = get('location.id', itemAvailability);
      const locationZoneId = get('zone.id', itemAvailability);
      const payload = {
        orderId,
        body: { itemId, locationId, locationZoneId, quantity }
      };
      dispatch(addPosOrderItem(payload));
    }
  };

  const updateOrderItem = orderItem => {
    const quantity = get('quantity', orderItem) + 1;
    if (orderStatus === COMPLETED) {
      dispatch(updateItemAdjustment({ orderItem, quantity }));
    } else {
      dispatch(updatePosOrderItemQuantity({ orderItem, quantity }));
    }
  };

  // Fetch posOrder by visitTrackId or create new
  useEffect(
    () => {
      if (!isFetchingOrder && !isOrderFetched) {
        dispatch(fetchPosOrderByVisitTrackId(visitTrackId));
      }
      if (isOrderFetched && !order && !fetchOrderError) {
        dispatch(createPosOrder({ visitTrackId }));
      }
    },
    [visitTrackId, isFetchingOrder, isOrderFetched, order]
  );

  useEffect(
    () => {
      if (trackingMethodId)
        dispatch(fetchPosLocationsForTrackingMethod(trackingMethodId));
    },
    [trackingMethodId]
  );

  useEffect(
    () => {
      if (clientId) dispatch(fetchClient(clientId));
      return () => {
        if (clientId) dispatch(removeGuest(clientId));
      };
    },
    [clientId]
  );

  // Add/Update order items when scan search result is only 1
  useEffect(
    () => {
      const isSingleResult = size(searchResults) === 1;
      if (isScanSearch && isFetchedResult && isSingleResult) {
        const itemAvailability = head(searchResults);
        if (itemAvailability.quantity > 0) {
          const orderItem = getOrderItemByAvailability(itemAvailability);
          if (orderItem) updateOrderItem(orderItem);
          else addOrderItem(itemAvailability);
        }
      }
    },
    [isScanSearch, isFetchedResult, searchResults]
  );

  useEffect(
    () => {
      if (orderStatus === COMPLETED) {
        const orderAdjustment = getLocalOrderAdjustment(order.id);
        dispatch(setOrderAdjustment(orderAdjustment));
      }
    },
    [orderStatus]
  );

  const handleSearch = (query, isScan = false) => {
    const cleanQuery = omitBy(isNull, query);
    setSearchQuery(cleanQuery);
    setIsScanSearch(isScan);

    if (size(cleanQuery.q) > 2) {
      const isScannedSameItem = isScan && query.q === searchQuery.q;
      if (isScannedSameItem) {
        const itemAvailability = head(searchResults);
        const orderItem = getOrderItemByAvailability(itemAvailability);
        if (orderItem) updateOrderItem(orderItem);
      } else {
        dispatch(clearPosSearchItems());
        dispatch(searchPosItems(cleanQuery));
      }
    }
  };

  const showSearchResult = size(searchQuery.q) > 2;

  if (fetchOrderError) {
    const defaultMsg = get('message', fetchOrderError);
    const errorMsg = getOr(defaultMsg, 'error.0.message', fetchOrderError);
    return <Message showIcon type="error" title={`ERROR: ${errorMsg}`} />;
  }

  return (
    <PosLayout>
      <Grid fluid>
        <Row style={{ marginBottom: 6, marginLeft: 6 }}>
          {guest ? (
            <Fragment>
              <StyledCol xs={24}>
                <GuestDetails guest={guest} />
              </StyledCol>
              {order && (
                <StyledCol xs={24}>
                  <HelpDesiredDetails>
                    Selected Help Desired:
                    <strong> {get('trackingMethod.name', order)}</strong>
                  </HelpDesiredDetails>
                </StyledCol>
              )}
            </Fragment>
          ) : (
            <Loader content="Loading guest details..." />
          )}
        </Row>
        <Row className="show-grid">
          <StyledCol xs={24}>
            {isSavingOrder && <StyledLoader backdrop vertical size="md" />}
            {order && (
              <SearchForm
                onSearch={handleSearch}
                startingLocation={startingLocation}
                customLocationOptions={customLocationOptions}
              />
            )}
            {showSearchResult && (
              <SearchResults
                isLoading={!isFetchedResult}
                order={order}
                data={searchResults}
              />
            )}
          </StyledCol>
          <StyledCol xs={24}>
            <OrderItems
              isLoading={!isOrderFetched}
              data={orderItems}
              orderStatus={orderStatus}
            />
          </StyledCol>
        </Row>
      </Grid>
    </PosLayout>
  );
}
